import React, { useMemo } from 'react';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	BarElement,
	Title,
	Tooltip,
	Legend,
	Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	BarElement,
	Title,
	Tooltip,
	Legend,
	Filler
);

// Memoized default options to prevent recreation on every render
const defaultOptions = {
	responsive: true,
	maintainAspectRatio: false,
	animation: false, // Disable animations for better performance
	plugins: {
		legend: {
			display: false,
		},
		tooltip: {
			mode: 'index',
			intersect: false,
			backgroundColor: 'rgba(255, 255, 255, 0.9)',
			titleColor: '#000',
			bodyColor: '#000',
			borderColor: '#ddd',
			borderWidth: 1,
			padding: 10,
			boxPadding: 4,
		},
	},
	scales: {
		x: {
			grid: {
				display: false,
			},
			ticks: {
				maxRotation: 0,
				autoSkip: true,
				maxTicksLimit: 6,
			},
		},
		y: {
			beginAtZero: true,
			grid: {
				color: 'rgba(0, 0, 0, 0.05)',
			},
		},
	},
	interaction: {
		mode: 'nearest',
		axis: 'x',
		intersect: false,
	},
};

// Optimized LineChart with memoization
export const LineChart = React.memo(({ data, markers }) => {
	const chartData = useMemo(() => {
		if (!data || !Array.isArray(data) || data.length === 0 || !markers || !Array.isArray(markers)) {
			return { labels: [], datasets: [] };
		}

		return {
			labels: data.map((item) => item.date),
			datasets: markers.map((marker) => {
				const key = marker.name.toLowerCase().replace(/\s+/g, '').replace(/\//g, '');
				
				return {
					label: marker.name,
					data: data.map((item) => {
						const value = item[key] !== undefined ? item[key] : (item.value || 0);
						return value;
					}),
					borderColor: marker.color,
					backgroundColor: marker.color + '26', // Adding 40 for 25% opacity
					fill: true,
					tension: 0,
					pointRadius: 2,
					pointHoverRadius: 5,
					borderWidth: 2,
				};
			}),
		};
	}, [data, markers]);

	// Memoize options to prevent unnecessary re-renders
	const options = useMemo(() => ({
		...defaultOptions,
		// Add any line-specific options here if needed
	}), []);

	if (!chartData.labels.length) {
		return (
			<div className="w-full h-full flex items-center justify-center text-gray-500">
				No data available
			</div>
		);
	}

	return (
		<div className="w-full h-full">
			<Line data={chartData} options={options} />
		</div>
	);
});

LineChart.displayName = 'LineChart';

// Optimized BarChart with memoization
export const BarChart = React.memo(({ data, markers }) => {
	const chartData = useMemo(() => {
		if (!data || !Array.isArray(data) || data.length === 0 || !markers || !Array.isArray(markers)) {
			return { labels: [], datasets: [] };
		}

		return {
			labels: data.map((item) => item.date),
			datasets: markers.map((marker) => {
				const key = marker.name.toLowerCase().replace(/\s+/g, '');
				
				return {
					label: marker.name,
					data: data.map((item) => {
						const value = item[key] !== undefined ? item[key] : (item.value || 0);
						return value;
					}),
					backgroundColor: marker.color,
					borderColor: marker.color,
					borderWidth: 1,
				};
			}),
		};
	}, [data, markers]);

	// Memoize options to prevent unnecessary re-renders
	const options = useMemo(() => ({
		...defaultOptions,
		// Add any bar-specific options here if needed
	}), []);

	if (!chartData.labels.length) {
		return (
			<div className="w-full h-full flex items-center justify-center text-gray-500">
				No data available
			</div>
		);
	}

	return (
		<div className="w-full h-full">
			<Bar data={chartData} options={options} />
		</div>
	);
});

BarChart.displayName = 'BarChart';
